/**
 * RoomLabelsLayer — one name + area label per room folder ("room tag"), shown
 * while the floorplan persona is active, in BOTH wall representations.
 *
 * Association is POSITIONAL, not linked: a tag's room is whichever room
 * polygon (of its floor) contains its anchor. Dragging a tag into another
 * room makes it that room's tag; a tag outside every room shows its name
 * only. Several tags may share one room. The same positional rule drives
 * sketch auto-filing (see resolveRoomForPoint).
 *
 * Folder data (id, name, parent floor) is pushed from React via `setFolders`
 * (the useRoomFolderData barrier) — the layer itself stays GraphQL-free.
 * Anchors live in the `room_group_position` property, keyed by folder id.
 *
 * Deliberately NOT parented under PointoramaRoom: the SKINNED (Perpendicular)
 * representation force-hides room objects (AnnotationBlacklistStack), but the
 * labels must survive there. The layer owns a group in the floorplan scene.
 *
 * Area source follows the representation switch:
 *  - ORIGINAL → the containing polygon's area;
 *  - SKINNED  → the interior bounded by the wall skin: polygon minus the
 *    floor's wall-partition rings (Manifold 2D boolean), falling back to the
 *    polygon area while the WASM module loads or when the floor has no sketch.
 */

import * as THREE from 'three';
import { TextSprite } from '../TextSprite';
import { Utils } from '../utils';
import { calculatePolygonArea2D } from './GeometryUtils';
import { computeEnclosedFaces, faceArea, faceContainingPoint } from './wallSketchFaces';
import type { FootprintPolygon } from './manifoldCsg';
import { pointInPolygonXY } from './resolveRoomForPoint';
import { getFloorWallSourceProperty, getRoomGroupPositionProperty, hasGlobalPropertyService, Subscription } from '@pointorama/potree-shared';
import { roomIdFromAutoFolderId } from '../../utils/roomFolders';
import type { AnnotationBlacklist } from '../viewer/AnnotationBlacklistStack';
import type { Viewer } from '../viewer/viewer';
import type { PointoramaRoom } from './PointoramaRoom';

/** Scene events after which the labels must be rebuilt. */
const REFRESH_EVENTS = ['room_added', 'room_removed', 'room_updated', 'edit_skeleton'] as const;

/** Room folder ("tag") info pushed from React state. */
export interface RoomFolderInfo {
  identifier: string;
  name: string;
  parentIdentifier: string | null;
}

export class RoomLabelsLayer {
  private viewer: Viewer;
  private group: THREE.Group | null = null;
  private folders: RoomFolderInfo[] = [];
  /** Folder whose label is hidden while its inline name editor is open. */
  private suppressedFolderId: string | null = null;
  /** Folders selected in the left panel — labels tinted in the accent color. */
  private selectedFolderIds = new Set<string>();
  private labels = new Map<string, TextSprite>();
  /** Per-folder anchor-position subscriptions (live-follow while a gizmo drags). */
  private positionSubs = new Map<string, Subscription>();
  /** Per-floor `floor_wall_source` subscriptions — the label AREA follows the
   *  floor's representation (measured polygon vs wall-sketch face). */
  private sourceSubs = new Map<string, Subscription>();
  private refreshListener: () => void;
  private personaListener: () => void;
  /** The persona whose mode changes we watch (labels are Design-tab-only). */
  private attachedPersona: { addEventListener(type: string, cb: () => void): void; removeEventListener(type: string, cb: () => void): void; getCurrentMode?(): { id: string } | undefined } | null = null;
  private representationUnsub: (() => void) | null = null;
  private refreshQueued = false;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.refreshListener = () => this.scheduleRefresh();
    this.personaListener = () => {
      this.attachActivePersona();
      this.scheduleRefresh();
    };
    for (const type of REFRESH_EVENTS) viewer.scene?.addEventListener(type, this.refreshListener);
    viewer.personaManager?.addEventListener('persona_manager_stack_changed', this.personaListener);
    // Areas re-render in the user's metric/imperial preference.
    viewer.addEventListener('length_unit_changed', this.refreshListener);
    this.representationUnsub = viewer.floorplanRepresentation?.subscribe(() => this.scheduleRefresh()) ?? null;
    this.attachActivePersona();
    this.scheduleRefresh();
  }

  /**
   * Track the active persona's mode switches (the labels only show in the
   * Design tab) — re-attached whenever the persona stack changes.
   */
  private attachActivePersona(): void {
    this.attachedPersona?.removeEventListener('mode_changed', this.refreshListener);
    this.attachedPersona = this.viewer.personaManager?.getActivePersona() ?? null;
    this.attachedPersona?.addEventListener('mode_changed', this.refreshListener);
  }

  /** Replace the folder list (pushed from React state) and refresh. */
  setFolders(folders: RoomFolderInfo[]): void {
    this.folders = folders;
    this.scheduleRefresh();
  }

  /** Current folder list (also consumed by resolveRoomForPoint). */
  getFolders(): readonly RoomFolderInfo[] {
    return this.folders;
  }

  /**
   * Hide one folder's label (or none) — used while its inline name editor
   * floats over the anchor, so the sprite doesn't show through the input.
   */
  setSuppressedFolder(folderId: string | null): void {
    if (this.suppressedFolderId === folderId) return;
    this.suppressedFolderId = folderId;
    this.scheduleRefresh();
  }

  /**
   * Folders currently selected in the left panel — their labels render in
   * the selection accent color. Pushed from React (redux selection).
   */
  setSelectedFolders(folderIds: readonly string[]): void {
    const next = new Set(folderIds);
    if (next.size === this.selectedFolderIds.size && [...next].every((id) => this.selectedFolderIds.has(id))) return;
    this.selectedFolderIds = next;
    this.scheduleRefresh();
  }

  /** Coalesce refresh triggers into one pass per microtask. */
  private scheduleRefresh(): void {
    if (this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      this.refresh();
    });
  }

  private ensureGroup(): THREE.Group | null {
    if (this.group) return this.group;
    const scene = this.viewer.floorplanTool?.scene;
    if (!scene) return null;
    this.group = new THREE.Group();
    this.group.name = 'room_folder_labels';
    scene.add(this.group);
    return this.group;
  }

  private isActive(): boolean {
    const persona = this.viewer.personaManager?.getActivePersona();
    return persona?.name === 'floorplan' && persona.getCurrentMode()?.id === 'design';
  }

  /**
   * A folder's stored anchor. Auto folders without one (created before
   * anchors existed) fall back to their source room's centroid until the
   * reconciler backfills the property; manual folders stay hidden until
   * placed.
   */
  private anchorForFolder(folder: RoomFolderInfo): { x: number; y: number; z: number } | null {
    try {
      const stored = getRoomGroupPositionProperty(folder.identifier).value;
      if (stored && (stored.x !== 0 || stored.y !== 0 || stored.z !== 0)) return stored;
    } catch {
      // property service hiccup
    }
    const sourceRoomId = roomIdFromAutoFolderId(folder.identifier);
    if (sourceRoomId) {
      const room = this.viewer.scene.rooms.find((r) => r.identifier === sourceRoomId);
      const polygon = room?.getPolygon() ?? [];
      if (polygon.length >= 3) {
        const sum = polygon.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y, z: acc.z + p.z }), { x: 0, y: 0, z: 0 });
        return { x: sum.x / polygon.length, y: sum.y / polygon.length, z: sum.z / polygon.length };
      }
    }
    return null;
  }

  /**
   * Smallest room polygon containing the anchor, or null. Scoped to the
   * folder's floor when known; a folder without a resolved parent (partial
   * cache states) matches rooms on any floor.
   */
  private containingRoom(folder: RoomFolderInfo, anchor: { x: number; y: number }): PointoramaRoom | null {
    let best: { room: PointoramaRoom; area: number } | null = null;
    for (const room of this.viewer.scene.rooms) {
      if (folder.parentIdentifier && room.groupIdentifier !== folder.parentIdentifier) continue;
      const polygon = room.getPolygon();
      if (polygon.length < 3 || !pointInPolygonXY(anchor.x, anchor.y, polygon)) continue;
      const area = calculatePolygonArea2D(polygon);
      if (!best || area < best.area) best = { room, area };
    }
    return best?.room ?? null;
  }

  /**
   * Area (m²) of the space the anchor sits in, per the representation:
   * MEASURED → the containing room-measure polygon; WALL SKETCH → the
   * enclosed wall-sketch face at the anchor (measures not required). Null
   * when the anchor sits in no space (label shows the name only).
   *
   * `facesBySketch` memoizes computeEnclosedFaces (a Manifold CSG pass over
   * the whole sketch) per refresh pass — F tags on one floor must not run F
   * identical CSG passes.
   */
  private areaForAnchor(folder: RoomFolderInfo, anchor: { x: number; y: number }, facesBySketch: Map<string, FootprintPolygon[] | null>, room: PointoramaRoom | null): number | null {
    const isOriginal = this.viewer.floorplanRepresentation?.isOriginalActive(folder.parentIdentifier) ?? true;
    if (isOriginal) {
      return room ? calculatePolygonArea2D(room.getPolygon()) : null;
    }
    // Strictly the folder's own floor: a null parent (transient cache state)
    // must NOT fall back to an arbitrary sketch — that computed another
    // floor's area on multi-floor projects.
    const sketch = folder.parentIdentifier ? this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === folder.parentIdentifier) : undefined;
    if (sketch) {
      let faces = facesBySketch.get(sketch.identifier);
      if (faces === undefined) {
        faces = computeEnclosedFaces(sketch);
        facesBySketch.set(sketch.identifier, faces);
      }
      const face = faces ? faceContainingPoint(faces, anchor) : null;
      if (face) return faceArea(face);
    }
    // No sketch (or space not enclosed) — fall back to the measure polygon.
    return room ? calculatePolygonArea2D(room.getPolygon()) : null;
  }

  private labelText(folder: RoomFolderInfo, area: number | null): string {
    // Ground-plan convention: tag name in capitals, area beneath. A tag
    // outside every space shows its name only.
    const name = (folder.name || 'Room').toUpperCase();
    if (area === null) return name;
    let suffix = '';
    const unit = this.viewer.lengthUnit;
    const display = this.viewer.lengthUnitDisplay;
    if (unit != null && display != null) {
      area = (area / Math.pow(unit.unitspermeter, 2)) * Math.pow(display.unitspermeter, 2);
      suffix = display.code;
    }
    return `${name}\n${area.toFixed(1)} ${suffix}²`;
  }

  /** Rebuild all labels from the current folders/rooms/representation. */
  refresh(): void {
    const group = this.ensureGroup();
    if (!group) return;

    const active = this.isActive() && hasGlobalPropertyService();
    group.visible = active;
    const seen = new Set<string>();

    // Subscribe to EVERY folder's anchor — including unplaced ones — so a tag
    // whose anchor write lands after the folder push still triggers a refresh
    // (otherwise a freshly created tag stays invisible until the next event).
    const folderIds = new Set(this.folders.map((f) => f.identifier));
    for (const folder of this.folders) {
      if (this.positionSubs.has(folder.identifier)) continue;
      try {
        this.positionSubs.set(folder.identifier, getRoomGroupPositionProperty(folder.identifier).subscribeToChanges(() => this.scheduleRefresh()));
      } catch {
        // retried on the next refresh
      }
    }
    for (const [folderId, sub] of this.positionSubs) {
      if (folderIds.has(folderId)) continue;
      sub.unsubscribe();
      this.positionSubs.delete(folderId);
    }

    // Follow each folder's FLOOR representation toggle — the area source
    // switches between measured polygons and wall-sketch faces with it.
    const floorIds = new Set<string>();
    for (const folder of this.folders) {
      if (folder.parentIdentifier) floorIds.add(folder.parentIdentifier);
    }
    for (const floorId of floorIds) {
      if (this.sourceSubs.has(floorId)) continue;
      try {
        this.sourceSubs.set(floorId, getFloorWallSourceProperty(floorId).subscribeToChanges(() => this.scheduleRefresh()));
      } catch {
        // retried on the next refresh
      }
    }
    for (const [floorId, sub] of this.sourceSubs) {
      if (floorIds.has(floorId)) continue;
      sub.unsubscribe();
      this.sourceSubs.delete(floorId);
    }

    if (active) {
      // One enclosed-faces (Manifold CSG) computation per sketch per pass,
      // shared by every tag on that floor.
      const facesBySketch = new Map<string, FootprintPolygon[] | null>();
      for (const folder of this.folders) {
        if (folder.identifier === this.suppressedFolderId) continue; // inline editor covers it
        const anchor = this.anchorForFolder(folder);
        if (!anchor) continue; // unplaced manual tag — nothing to render yet
        seen.add(folder.identifier);

        let label = this.labels.get(folder.identifier);
        if (!label) {
          label = new TextSprite('');
          // Ground-plan styling: bare lettering, no badge — white fill with the
          // sprite's thin dark outline keeps it legible on both the light Plan
          // view and the dark X-Ray background.
          label.borderThickness = 2;
          label.setTextColor({ r: 245, g: 245, b: 245, a: 1.0 });
          label.setBorderColor({ r: 0, g: 0, b: 0, a: 0 });
          label.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0 });
          label.fontsize = 16;
          label.material.depthTest = false;
          label.renderOrder = 5;
          group.add(label);
          this.labels.set(folder.identifier, label);
        }
        // Selection accent: neon-green lettering (matches the app accent /
        // hover highlight) so the picked tag reads as selected in the viewport.
        const selected = this.selectedFolderIds.has(folder.identifier);
        if (label.userData.selected !== selected) {
          label.userData.selected = selected;
          label.setTextColor(selected ? { r: 110, g: 231, b: 160, a: 1.0 } : { r: 245, g: 245, b: 245, a: 1.0 });
        }
        // Cache the positional room association — the render-time floor lock
        // (collectFloorLockedSprites) inherits the room measure's hidden state
        // from it without re-running point-in-polygon every frame.
        const room = this.containingRoom(folder, anchor);
        label.userData.roomIdentifier = room?.identifier ?? null;
        label.setText(this.labelText(folder, this.areaForAnchor(folder, anchor, facesBySketch, room)));
        label.position.set(anchor.x, anchor.y, anchor.z);
        label.visible = true;
      }
    }

    // Drop labels for folders that vanished (or lost their anchor); their
    // anchor subscriptions are pruned above against the folder list.
    for (const [folderId, label] of this.labels) {
      if (active && seen.has(folderId)) continue;
      group.remove(label);
      this.labels.delete(folderId);
    }

    // Scale freshly created labels immediately — updateScales otherwise only
    // runs on camera changes, leaving new sprites huge until the next orbit.
    if (active && this.labels.size > 0) {
      const domElement = this.viewer.renderer.domElement;
      this.updateScales(this.viewer.scene.getActiveCamera(), domElement.clientWidth, domElement.clientHeight);
    }
  }

  /**
   * Room tags of one floor for drawing exports (DXF / PDF): the same text and
   * anchor the on-screen label shows, computed on demand — independent of the
   * Design tab being open. Tags of hidden rooms are left out, like on screen.
   */
  exportTags(floorId: string): { folderId: string; position: { x: number; y: number; z: number }; text: string }[] {
    const facesBySketch = new Map<string, FootprintPolygon[] | null>();
    const tags: { folderId: string; position: { x: number; y: number; z: number }; text: string }[] = [];
    for (const folder of this.folders) {
      if (folder.parentIdentifier !== floorId) continue;
      const anchor = this.anchorForFolder(folder);
      if (!anchor) continue;
      const room = this.containingRoom(folder, anchor);
      if (room?.visible === false) continue;
      tags.push({ folderId: folder.identifier, position: anchor, text: this.labelText(folder, this.areaForAnchor(folder, anchor, facesBySketch, room)) });
    }
    return tags;
  }

  /**
   * Identity-only blacklist check for a tag. A tag is a group, not a typed
   * annotation, so only the identity facet applies — matched against the
   * folder's own id or its parent floor id (exactly how the floor-isolation
   * whitelist scopes every other floor object). Type/opening facets are
   * ignored.
   */
  private isFilteredOut(folder: RoomFolderInfo, bl: AnnotationBlacklist | null): boolean {
    if (!bl?.identifiers || bl.identifiers.size === 0) return false;
    const matches = bl.identifiers.has(folder.identifier) || (!!folder.parentIdentifier && bl.identifiers.has(folder.parentIdentifier));
    return bl.mode === 'whitelist' ? !matches : matches;
  }

  /** True when the tag's positionally-associated room measure is user-hidden
   *  (eye toggle) — the tag and its area reading hide with it. */
  private isRoomHidden(label: TextSprite): boolean {
    const roomId = label.userData.roomIdentifier as string | null | undefined;
    if (!roomId) return false;
    return this.viewer.scene.rooms.find((r) => r.identifier === roomId)?.visible === false;
  }

  /**
   * Sprites the render-time blacklist must hide this frame: tags whose floor
   * the view's filter excludes (floor isolation) or whose room measure is
   * hidden — room tags floor-lock like every other floor object. Consumed by
   * applyAnnotationBlacklist, which flips `.visible` frame-locally.
   */
  collectFloorLockedSprites(bl: AnnotationBlacklist | null): TextSprite[] {
    if (!this.group?.visible) return [];
    const locked: TextSprite[] = [];
    for (const folder of this.folders) {
      const label = this.labels.get(folder.identifier);
      if (!label?.visible) continue;
      if (this.isFilteredOut(folder, bl) || this.isRoomHidden(label)) locked.push(label);
    }
    return locked;
  }

  /**
   * Current label anchors (world position + folder), for gizmo hit-testing.
   * Floor-locked tags are excluded: the render blacklist only hides them
   * frame-locally, so picking — which runs between frames — must apply the
   * same filter (mirrors isAnnotationPickBlocked, incl. using the main view).
   */
  getLabelEntries(): { folderId: string; position: THREE.Vector3 }[] {
    if (!this.group?.visible) return [];
    const bl = this.viewer.renderViews[0]?.viewState?.getAnnotationBlacklist() ?? null;
    const entries: { folderId: string; position: THREE.Vector3 }[] = [];
    for (const [folderId, label] of this.labels) {
      if (!label.visible || this.isRoomHidden(label)) continue;
      const folder = this.folders.find((f) => f.identifier === folderId);
      if (folder && this.isFilteredOut(folder, bl)) continue;
      entries.push({ folderId, position: label.position.clone() });
    }
    return entries;
  }

  /** Camera-distance label scaling — call from the viewer loop on camera change. */
  updateScales(camera: THREE.Camera, clientWidth: number, clientHeight: number): void {
    if (!this.group?.visible) return;
    for (const label of this.labels.values()) {
      const distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      label.scale.set(scale, scale, scale);
    }
  }

  dispose(): void {
    for (const type of REFRESH_EVENTS) this.viewer.scene?.removeEventListener(type, this.refreshListener);
    this.viewer.personaManager?.removeEventListener('persona_manager_stack_changed', this.personaListener);
    this.viewer.removeEventListener('length_unit_changed', this.refreshListener);
    this.attachedPersona?.removeEventListener('mode_changed', this.refreshListener);
    this.attachedPersona = null;
    this.representationUnsub?.();
    this.representationUnsub = null;
    for (const sub of this.positionSubs.values()) sub.unsubscribe();
    this.positionSubs.clear();
    for (const sub of this.sourceSubs.values()) sub.unsubscribe();
    this.sourceSubs.clear();
    if (this.group) {
      this.group.parent?.remove(this.group);
      this.group = null;
    }
    this.labels.clear();
  }
}
